import { useRef, useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Slider from '@mui/material/Slider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';

// ---------------------------------------------------------------------------
// Constants & pure helpers (no React dependencies)
// ---------------------------------------------------------------------------

const SVG_NS = 'http://www.w3.org/2000/svg';
const SVG_SIZE = 400;
const HANDLE_SIZE = 10;

interface ObjProps {
    rotation?: number;
    imgSrc?: string;
    text?: string;
    fontSize?: number;
    fontFamily?: string;
    color?: string;
    fill?: boolean;
    strokeEnabled?: boolean;
    strokeWidth?: number;
    strokeColor?: string;
}

interface SvgObj {
    id: number;
    type: 'image' | 'text' | 'circle' | 'rectangle' | 'star';
    x: number;
    y: number;
    width: number;
    height: number;
    properties: ObjProps;
}

function contains(obj: SvgObj, x: number, y: number) {
    return x >= obj.x && x <= obj.x + obj.width && y >= obj.y && y <= obj.y + obj.height;
}

function cornerHandles(obj: SvgObj) {
    return {
        nw: { x: obj.x, y: obj.y },
        ne: { x: obj.x + obj.width, y: obj.y },
        sw: { x: obj.x, y: obj.y + obj.height },
        se: { x: obj.x + obj.width, y: obj.y + obj.height },
    } as const;
}

function cornerHandleAt(obj: SvgObj, x: number, y: number): string | null {
    const t = HANDLE_SIZE + 3;
    for (const [c, p] of Object.entries(cornerHandles(obj))) {
        if (Math.abs(x - p.x) <= t && Math.abs(y - p.y) <= t) return c;
    }
    return null;
}

function starPath(cx: number, cy: number, r: number): string {
    let d = '';
    let a = -Math.PI / 2;
    const step = Math.PI / 5;
    for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? r : r * 0.45;
        const x = cx + Math.cos(a) * radius;
        const y = cy + Math.sin(a) * radius;
        d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
        a += step;
    }
    return d + ' Z';
}

function mkEl<T extends SVGElement>(tag: string): T {
    return document.createElementNS(SVG_NS, tag) as T;
}

function drawObject(svg: SVGSVGElement, obj: SvgObj, isSelected: boolean) {
    const rot = obj.properties.rotation || 0;
    const cx = obj.x + obj.width / 2;
    const cy = obj.y + obj.height / 2;

    let el: SVGElement | null = null;

    if (obj.type === 'image') {
        el = mkEl('image');
        el.setAttribute('href', obj.properties.imgSrc!);
        el.setAttribute('x', String(obj.x));
        el.setAttribute('y', String(obj.y));
        el.setAttribute('width', String(obj.width));
        el.setAttribute('height', String(obj.height));
    } else if (obj.type === 'text') {
        el = mkEl('text');
        el.setAttribute('x', String(obj.x));
        el.setAttribute('y', String(obj.y + (obj.properties.fontSize || 20)));
        el.setAttribute('font-size', String(obj.properties.fontSize || 20));
        el.setAttribute('font-family', obj.properties.fontFamily || 'Arial');
        el.setAttribute('fill', obj.properties.color || '#000');
        el.textContent = obj.properties.text || '';
    } else if (obj.type === 'circle') {
        el = mkEl('circle');
        el.setAttribute('cx', String(cx));
        el.setAttribute('cy', String(cy));
        el.setAttribute('r', String(obj.width / 2));
        el.setAttribute('fill', obj.properties.fill ? (obj.properties.color || '#000') : 'none');
        if (obj.properties.strokeEnabled) {
            el.setAttribute('stroke', obj.properties.strokeColor || '#000');
            el.setAttribute('stroke-width', String(obj.properties.strokeWidth || 1));
        }
    } else if (obj.type === 'rectangle') {
        el = mkEl('rect');
        el.setAttribute('x', String(obj.x));
        el.setAttribute('y', String(obj.y));
        el.setAttribute('width', String(obj.width));
        el.setAttribute('height', String(obj.height));
        el.setAttribute('fill', obj.properties.fill ? (obj.properties.color || '#000') : 'none');
        if (obj.properties.strokeEnabled) {
            el.setAttribute('stroke', obj.properties.strokeColor || '#000');
            el.setAttribute('stroke-width', String(obj.properties.strokeWidth || 1));
        }
    } else if (obj.type === 'star') {
        el = mkEl('path');
        el.setAttribute('d', starPath(cx, cy, obj.width / 2));
        el.setAttribute('fill', obj.properties.fill ? (obj.properties.color || '#000') : 'none');
        if (obj.properties.strokeEnabled) {
            el.setAttribute('stroke', obj.properties.strokeColor || '#000');
            el.setAttribute('stroke-width', String(obj.properties.strokeWidth || 1));
        }
    }

    if (!el) return;

    if (rot !== 0) el.setAttribute('transform', `rotate(${rot} ${cx} ${cy})`);
    el.setAttribute('data-id', String(obj.id));
    (el as SVGElement & { style: CSSStyleDeclaration }).style.cursor = 'pointer';
    svg.appendChild(el);

    if (isSelected) drawSelectionBox(svg, obj);
}

function drawSelectionBox(svg: SVGSVGElement, obj: SvgObj) {
    const g = mkEl<SVGGElement>('g');

    const border = mkEl<SVGRectElement>('rect');
    border.setAttribute('x', String(obj.x - 2));
    border.setAttribute('y', String(obj.y - 2));
    border.setAttribute('width', String(obj.width + 4));
    border.setAttribute('height', String(obj.height + 4));
    border.setAttribute('fill', 'none');
    border.setAttribute('stroke', '#007bff');
    border.setAttribute('stroke-width', '2');
    border.setAttribute('stroke-dasharray', '5,5');
    border.setAttribute('pointer-events', 'none');
    g.appendChild(border);

    if (obj.type !== 'text') {
        for (const [corner, pos] of Object.entries(cornerHandles(obj))) {
            const h = mkEl<SVGRectElement>('rect');
            h.setAttribute('x', String(pos.x - HANDLE_SIZE / 2));
            h.setAttribute('y', String(pos.y - HANDLE_SIZE / 2));
            h.setAttribute('width', String(HANDLE_SIZE));
            h.setAttribute('height', String(HANDLE_SIZE));
            h.setAttribute('fill', '#ff6b6b');
            h.setAttribute('stroke', 'white');
            h.setAttribute('stroke-width', '2');
            h.setAttribute('class', `handle-${corner}`);
            h.setAttribute('pointer-events', 'none');
            g.appendChild(h);
        }
    }

    svg.appendChild(g);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BadgeEditorProps {
    open: boolean;
    onClose: () => void;
    /** Called with the PNG File and a local blob URL when user clicks "Use Image" */
    onExport: (file: File, previewUrl: string) => void;
    /** If provided, pre-loads this image URL onto the canvas when the editor opens */
    initialImageUrl?: string | null;
}

export default function BadgeEditor({ open, onClose, onExport, initialImageUrl }: BadgeEditorProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const nextId = useRef(1);

    // Mutable editor state — stored in refs to avoid re-render overhead during drag
    const objectsRef = useRef<SvgObj[]>([]);
    const selectedRef = useRef<SvgObj | null>(null);
    const isDragging = useRef(false);
    const isResizing = useRef(false);
    const resizeHandle = useRef<string | null>(null);
    const dragOffset = useRef({ x: 0, y: 0 });

    // Tool settings (React state — drives the UI panel)
    const [textVal, setTextVal] = useState('');
    const [fontSize, setFontSize] = useState(24);
    const [fontFamily, setFontFamily] = useState('Arial');
    const [textColor, setTextColor] = useState('#ffffff');
    const [shapeColor, setShapeColor] = useState('#4285f4');
    const [fillShape, setFillShape] = useState(true);
    const [strokeEnabled, setStrokeEnabled] = useState(false);
    const [strokeWidth, setStrokeWidth] = useState(2);
    const [strokeColor, setStrokeColor] = useState('#000000');

    // Selected object panel (React state)
    const [selType, setSelType] = useState<string | null>(null);
    const [objX, setObjX] = useState(0);
    const [objY, setObjY] = useState(0);
    const [objW, setObjW] = useState(0);
    const [objH, setObjH] = useState(0);
    const [rotation, setRotation] = useState(0);
    const [layerInfo, setLayerInfo] = useState('');

    const makeId = () => nextId.current++;

    // ---- Redraw ---------------------------------------------------------------

    const redraw = useCallback(() => {
        const svg = svgRef.current;
        if (!svg) return;
        svg.innerHTML = '';
        const sel = selectedRef.current;
        for (const obj of objectsRef.current) {
            drawObject(svg, obj, sel?.id === obj.id);
        }
    }, []);

    // ---- Sync panel from selected object -------------------------------------

    const syncPanel = useCallback(() => {
        const obj = selectedRef.current;
        if (!obj) {
            setSelType(null);
            setLayerInfo('');
            return;
        }
        const idx = objectsRef.current.findIndex(o => o.id === obj.id);
        setSelType(obj.type);
        setObjX(Math.round(obj.x));
        setObjY(Math.round(obj.y));
        setObjW(Math.round(obj.width));
        setObjH(Math.round(obj.height));
        setRotation(obj.properties.rotation || 0);
        setLayerInfo(`Layer ${idx + 1} of ${objectsRef.current.length}`);
        if (obj.type === 'text') {
            setTextVal(obj.properties.text || '');
            setFontSize(obj.properties.fontSize || 24);
            setFontFamily(obj.properties.fontFamily || 'Arial');
            setTextColor(obj.properties.color || '#ffffff');
        }
    }, []);

    const select = useCallback((obj: SvgObj | null) => {
        selectedRef.current = obj;
        syncPanel();
        redraw();
    }, [syncPanel, redraw]);

    // ---- Pointer events (pointer capture keeps events routed to SVG even when cursor leaves) ----

    const handlePointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
        e.preventDefault();
        const svg = svgRef.current;
        if (!svg) return;
        svg.setPointerCapture(e.pointerId);

        const r = svg.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;
        const sel = selectedRef.current;

        // Check resize handles first
        if (sel && sel.type !== 'text') {
            const h = cornerHandleAt(sel, x, y);
            if (h) { isResizing.current = true; resizeHandle.current = h; return; }
        }

        // Hit-test objects back-to-front
        for (let i = objectsRef.current.length - 1; i >= 0; i--) {
            if (contains(objectsRef.current[i], x, y)) {
                const obj = objectsRef.current[i];
                selectedRef.current = obj;
                isDragging.current = true;
                dragOffset.current = { x: x - obj.x, y: y - obj.y };
                syncPanel();
                redraw();
                return;
            }
        }

        selectedRef.current = null;
        syncPanel();
        redraw();
    }, [syncPanel, redraw]);

    const handlePointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
        const svg = svgRef.current;
        if (!svg) return;
        const r = svg.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;
        const sel = selectedRef.current;

        if (isResizing.current && sel && sel.type !== 'text' && resizeHandle.current) {
            const h = resizeHandle.current;
            const ox = sel.x, oy = sel.y, ow = sel.width, oh = sel.height;
            const min = 20;
            if (h === 'se') { sel.width = Math.max(min, x - ox); sel.height = Math.max(min, y - oy); }
            else if (h === 'sw') { sel.width = Math.max(min, ox + ow - x); sel.x = x; sel.height = Math.max(min, y - oy); }
            else if (h === 'ne') { sel.width = Math.max(min, x - ox); sel.height = Math.max(min, oy + oh - y); sel.y = y; }
            else if (h === 'nw') { sel.width = Math.max(min, ox + ow - x); sel.x = x; sel.height = Math.max(min, oy + oh - y); sel.y = y; }
            sel.x = Math.max(0, Math.min(sel.x, SVG_SIZE - sel.width));
            sel.y = Math.max(0, Math.min(sel.y, SVG_SIZE - sel.height));
            syncPanel();
            redraw();
            return;
        }

        if (isDragging.current && sel) {
            sel.x = Math.max(0, Math.min(x - dragOffset.current.x, SVG_SIZE - sel.width));
            sel.y = Math.max(0, Math.min(y - dragOffset.current.y, SVG_SIZE - sel.height));
            syncPanel();
            redraw();
        }

        // Cursor feedback (coords are relative to SVG since we have pointer capture)
        if (!sel) { svg.style.cursor = 'crosshair'; return; }
        const h = sel.type !== 'text' ? cornerHandleAt(sel, x, y) : null;
        const cursorMap: Record<string, string> = { nw: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', se: 'nwse-resize' };
        svg.style.cursor = h ? cursorMap[h] : contains(sel, x, y) ? 'move' : 'crosshair';
    }, [syncPanel, redraw]);

    const handlePointerUp = useCallback(() => {
        isDragging.current = false;
        isResizing.current = false;
        resizeHandle.current = null;
    }, []);

    // ---- Initialize when dialog opens ----------------------------------------

    useEffect(() => {
        if (!open) return;
        objectsRef.current = [];
        selectedRef.current = null;

        if (initialImageUrl) {
            const img = new Image();
            img.onload = () => {
                const maxSide = SVG_SIZE * 0.8;
                let w = img.width, h = img.height;
                const ratio = Math.min(maxSide / w, maxSide / h, 1);
                w = Math.round(w * ratio); h = Math.round(h * ratio);
                objectsRef.current = [{
                    id: makeId(), type: 'image',
                    x: Math.round((SVG_SIZE - w) / 2), y: Math.round((SVG_SIZE - h) / 2),
                    width: w, height: h,
                    properties: { imgSrc: initialImageUrl },
                }];
                syncPanel();
                redraw();
            };
            img.src = initialImageUrl;
        } else {
            syncPanel();
            redraw();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // ---- Tool actions ---------------------------------------------------------

    const addShape = (type: 'circle' | 'rectangle' | 'star') => {
        const obj: SvgObj = {
            id: makeId(), type, x: 100, y: 100, width: 120, height: 120,
            properties: { color: shapeColor, fill: fillShape, strokeEnabled, strokeWidth, strokeColor },
        };
        objectsRef.current.push(obj);
        select(obj);
    };

    const handleTextAction = () => {
        if (!textVal.trim()) return;
        const sel = selectedRef.current;
        if (sel?.type === 'text') {
            sel.properties = { ...sel.properties, text: textVal, fontSize, fontFamily, color: textColor };
        } else {
            const obj: SvgObj = {
                id: makeId(), type: 'text', x: 50, y: 50, width: 220, height: fontSize + 10,
                properties: { text: textVal, fontSize, fontFamily, color: textColor },
            };
            objectsRef.current.push(obj);
            select(obj);
            return;
        }
        redraw();
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const src = ev.target?.result as string;
            const img = new Image();
            img.onload = () => {
                const maxSide = SVG_SIZE * 0.6;
                let w = img.width, h = img.height;
                const ratio = Math.min(maxSide / w, maxSide / h, 1);
                w = Math.round(w * ratio); h = Math.round(h * ratio);
                const obj: SvgObj = {
                    id: makeId(), type: 'image',
                    x: Math.round((SVG_SIZE - w) / 2), y: Math.round((SVG_SIZE - h) / 2),
                    width: w, height: h,
                    properties: { imgSrc: src },
                };
                objectsRef.current.push(obj);
                select(obj);
            };
            img.src = src;
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const deleteSelected = () => {
        if (!selectedRef.current) return;
        objectsRef.current = objectsRef.current.filter(o => o.id !== selectedRef.current!.id);
        selectedRef.current = null;
        syncPanel();
        redraw();
    };

    const layerOp = (op: 'front' | 'back' | 'up' | 'down') => {
        const obj = selectedRef.current;
        if (!obj) return;
        const arr = objectsRef.current;
        const i = arr.findIndex(o => o.id === obj.id);
        if (op === 'front' && i > -1) { arr.splice(i, 1); arr.push(obj); }
        else if (op === 'back' && i > -1) { arr.splice(i, 1); arr.unshift(obj); }
        else if (op === 'up' && i < arr.length - 1) { [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; }
        else if (op === 'down' && i > 0) { [arr[i], arr[i - 1]] = [arr[i - 1], arr[i]]; }
        syncPanel();
        redraw();
    };

    const setRotationVal = (angle: number) => {
        const sel = selectedRef.current;
        if (!sel) return;
        sel.properties.rotation = angle;
        setRotation(angle);
        redraw();
    };

    const setPosVal = (field: 'x' | 'y' | 'width' | 'height', val: number) => {
        const sel = selectedRef.current;
        if (!sel) return;
        sel[field] = field === 'width' || field === 'height' ? Math.max(20, val) : val;
        syncPanel();
        redraw();
    };

    const clearCanvas = () => {
        if (!window.confirm('Clear the canvas? This cannot be undone.')) return;
        objectsRef.current = [];
        selectedRef.current = null;
        syncPanel();
        redraw();
    };

    const handleExport = () => {
        const svg = svgRef.current;
        if (!svg) return;
        selectedRef.current = null;
        redraw();

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        canvas.width = SVG_SIZE;
        canvas.height = SVG_SIZE;
        const ctx = canvas.getContext('2d', { alpha: true })!;
        const img = new Image();
        img.onload = () => {
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(blob => {
                if (!blob) return;
                const file = new File([blob], 'badge.png', { type: 'image/png' });
                const url = URL.createObjectURL(blob);
                onExport(file, url);
                onClose();
            }, 'image/png');
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    // ---- Render ---------------------------------------------------------------

    const isShapeType = ['circle', 'rectangle', 'star'].includes(selType ?? '');
    const isTextType = selType === 'text';

    const colorInput = (value: string, onChange: (v: string) => void) => (
        <Box display="flex" alignItems="center" gap={1}>
            <input type="color" value={value} onChange={e => onChange(e.target.value)}
                style={{ width: 32, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', padding: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{value}</Typography>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false}
            PaperProps={{ sx: { width: '92vw', maxWidth: 900, height: '90vh', display: 'flex', flexDirection: 'column' } }}>
            <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>Badge Image Editor</DialogTitle>

            <DialogContent sx={{ flex: 1, display: 'flex', gap: 2, p: 2, overflow: 'hidden' }}>
                {/* Canvas area */}
                <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    {/* Checkerboard background to show transparency */}
                    <Box sx={{
                        width: SVG_SIZE, height: SVG_SIZE, borderRadius: 1, overflow: 'hidden',
                        background: 'repeating-conic-gradient(#444 0% 25%, #2a2a2a 0% 50%) 0 0 / 16px 16px',
                        border: '1px solid', borderColor: 'divider',
                    }}>
                        <svg ref={svgRef} width={SVG_SIZE} height={SVG_SIZE}
                            viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                            style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {SVG_SIZE}×{SVG_SIZE} · transparent background
                    </Typography>
                </Box>

                {/* Tools panel */}
                <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pr: 0.5 }}>

                    {/* Image upload */}
                    <Accordion defaultExpanded disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">Image</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <input ref={fileInputRef} type="file" accept="image/*"
                                style={{ display: 'none' }} onChange={handleImageUpload} />
                            <Button fullWidth onClick={() => fileInputRef.current?.click()}>
                                Upload Image
                            </Button>
                        </AccordionDetails>
                    </Accordion>

                    {/* Text */}
                    <Accordion defaultExpanded disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">{isTextType ? 'Edit Text' : 'Add Text'}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField label="Text" value={textVal} size="small" fullWidth
                                onChange={e => setTextVal(e.target.value)} />
                            <Box display="flex" gap={1} alignItems="center">
                                <TextField label="Size" type="number" value={fontSize} size="small"
                                    onChange={e => setFontSize(Number(e.target.value))} sx={{ width: 72 }} />
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Font</InputLabel>
                                    <Select label="Font" value={fontFamily} onChange={e => setFontFamily(e.target.value)}>
                                        {['Arial', 'Georgia', 'Verdana', 'Impact', 'Times New Roman', 'Courier New'].map(f =>
                                            <MenuItem key={f} value={f}>{f}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">Color</Typography>
                                {colorInput(textColor, v => {
                                    setTextColor(v);
                                    const sel = selectedRef.current;
                                    if (sel?.type === 'text') { sel.properties.color = v; redraw(); }
                                })}
                            </Box>
                            <Box display="flex" gap={1}>
                                <Button variant="contained" size="small" fullWidth
                                    disabled={!textVal.trim()} onClick={handleTextAction}>
                                    {isTextType ? 'Update Text' : 'Add Text'}
                                </Button>
                                {isTextType && (
                                    <Button size="small" onClick={() => {
                                        selectedRef.current = null;
                                        syncPanel();
                                        redraw();
                                    }}>Cancel</Button>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Shapes */}
                    <Accordion defaultExpanded disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">Shapes</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">Fill color</Typography>
                                {colorInput(shapeColor, v => {
                                    setShapeColor(v);
                                    const sel = selectedRef.current;
                                    if (sel && isShapeType) { sel.properties.color = v; redraw(); }
                                })}
                            </Box>
                            <FormControlLabel label="Fill" control={<Switch size="small" checked={fillShape}
                                onChange={e => {
                                    setFillShape(e.target.checked);
                                    const sel = selectedRef.current;
                                    if (sel && isShapeType) { sel.properties.fill = e.target.checked; redraw(); }
                                }} />} />
                            <FormControlLabel label="Stroke" control={<Switch size="small" checked={strokeEnabled}
                                onChange={e => {
                                    setStrokeEnabled(e.target.checked);
                                    const sel = selectedRef.current;
                                    if (sel && isShapeType) { sel.properties.strokeEnabled = e.target.checked; redraw(); }
                                }} />} />
                            {strokeEnabled && (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>{strokeWidth}px</Typography>
                                    <Slider size="small" min={1} max={20} value={strokeWidth} sx={{ flex: 1 }}
                                        onChange={(_, v) => {
                                            setStrokeWidth(v as number);
                                            const sel = selectedRef.current;
                                            if (sel && isShapeType) { sel.properties.strokeWidth = v as number; redraw(); }
                                        }} />
                                    {colorInput(strokeColor, v => {
                                        setStrokeColor(v);
                                        const sel = selectedRef.current;
                                        if (sel && isShapeType) { sel.properties.strokeColor = v; redraw(); }
                                    })}
                                </Box>
                            )}
                            <ButtonGroup fullWidth>
                                <Button onClick={() => addShape('circle')}>Circle</Button>
                                <Button onClick={() => addShape('rectangle')}>Rect</Button>
                                <Button onClick={() => addShape('star')}>Star</Button>
                            </ButtonGroup>
                        </AccordionDetails>
                    </Accordion>

                    {/* Selected object panel */}
                    {selType && (
                        <Accordion defaultExpanded disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">
                                    Selected — {selType.charAt(0).toUpperCase() + selType.slice(1)}
                                    {layerInfo && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({layerInfo})</Typography>}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {selType !== 'text' && (
                                    <>
                                        <Box display="flex" gap={1}>
                                            <TextField label="X" type="number" value={objX} size="small" sx={{ flex: 1 }}
                                                onChange={e => { setObjX(+e.target.value); setPosVal('x', +e.target.value); }} />
                                            <TextField label="Y" type="number" value={objY} size="small" sx={{ flex: 1 }}
                                                onChange={e => { setObjY(+e.target.value); setPosVal('y', +e.target.value); }} />
                                            <TextField label="W" type="number" value={objW} size="small" sx={{ flex: 1 }}
                                                onChange={e => { setObjW(+e.target.value); setPosVal('width', +e.target.value); }} />
                                            <TextField label="H" type="number" value={objH} size="small" sx={{ flex: 1 }}
                                                onChange={e => { setObjH(+e.target.value); setPosVal('height', +e.target.value); }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Rotation: {rotation}°</Typography>
                                            <Slider size="small" min={0} max={359} value={rotation}
                                                onChange={(_, v) => setRotationVal(v as number)} />
                                            <ButtonGroup>
                                                <Button onClick={() => setRotationVal(((rotation - 45) + 360) % 360)}>−45°</Button>
                                                <Button onClick={() => setRotationVal((rotation + 45) % 360)}>+45°</Button>
                                                <Button onClick={() => setRotationVal(0)}>Reset</Button>
                                            </ButtonGroup>
                                        </Box>
                                    </>
                                )}

                                {/* Layers */}
                                <ButtonGroup>
                                    <Button onClick={() => layerOp('front')}>Front</Button>
                                    <Button onClick={() => layerOp('back')}>Back</Button>
                                    <Button onClick={() => layerOp('up')}>↑</Button>
                                    <Button onClick={() => layerOp('down')}>↓</Button>
                                </ButtonGroup>

                                <Button color="error" startIcon={<DeleteIcon />} onClick={deleteSelected}>
                                    Delete
                                </Button>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 2, flexShrink: 0 }}>
                <Button color="error" onClick={clearCanvas}>Clear</Button>
                <Box flex={1} />
                <Button color="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleExport}>Use Image</Button>
            </DialogActions>
        </Dialog>
    );
}
